/**
 * AuthenticatedRoute Component
 *
 * Protects routes that require authentication only (no subscription check).
 * Used in Dashboard-First architecture where users can access dashboard
 * without an active subscription.
 *
 * Issue #1456: Dashboard-First Phase 1
 * ADR-0014: Dashboard-First Architecture
 *
 * Flow:
 * 1. Wait for auth initialization (Issue #633 pattern - CRITICAL)
 * 2. Check authentication -> redirect to /login if not authenticated
 * 3. Render children if authenticated (NO subscription check)
 *
 * @module components/auth/AuthenticatedRoute
 */

import React from 'react';
import { Navigate, Outlet } from 'react-router-dom';
import { useUser as useClerkUser } from '@clerk/clerk-react';
import { useAuthContext } from '../../hooks/useAuth';
import { useMockUser } from '../../mocks/MockClerkProvider';
import logger from '../../utils/logger';

interface AuthenticatedRouteProps {
  /** Child components to render (optional - uses Outlet if not provided) */
  children?: React.ReactNode;
}

/**
 * Route protection component that requires authentication only
 * (no subscription check - used for Dashboard-First flow)
 *
 * @example
 * ```tsx
 * // Wrap a route with AuthenticatedRoute
 * <Route
 *   element={
 *     <AuthenticatedRoute>
 *       <DashboardLayout />
 *     </AuthenticatedRoute>
 *   }
 * >
 *   <Route path="/dashboard" element={<Dashboard />} />
 * </Route>
 * ```
 */
export const AuthenticatedRoute: React.FC<AuthenticatedRouteProps> = ({
  children,
}) => {
  const { isAuthenticated, isInitializing: isAuthInitializing } =
    useAuthContext();

  // Issue #1764: Clerk SDK初期化状態を直接チェック（二重保護）
  // useAuth の isInitializing だけでなく、Clerk SDK の isLoaded も直接確認する
  const enableDevLogin = import.meta.env.VITE_ENABLE_DEV_LOGIN;
  const mode = import.meta.env.MODE;
  const isDevMode = mode === 'development' && enableDevLogin === 'true';

  const clerkData = useClerkUser();
  const mockClerkData = useMockUser();
  const { isLoaded: clerkIsLoaded, isSignedIn: clerkIsSignedIn } = isDevMode
    ? mockClerkData
    : clerkData;

  // Debug logging
  logger.debug('[AuthenticatedRoute] State:', {
    isAuthInitializing,
    isAuthenticated,
    clerkIsLoaded,
    clerkIsSignedIn,
    currentPath:
      typeof window !== 'undefined' ? window.location.pathname : 'unknown',
  });

  // CRITICAL (Issue #633 + #1764 + #1758): Wait for ALL conditions:
  // 1. useAuth hook initialization complete
  // 2. Clerk SDK initialization complete
  // 3. Clerk auth state determined (not undefined)
  // Issue #1758: clerkIsSignedIn === undefined means Clerk is still determining auth state
  // This prevents flash where redirect triggers before auth state is fully determined
  if (isAuthInitializing || !clerkIsLoaded || clerkIsSignedIn === undefined) {
    logger.debug('[AuthenticatedRoute] Waiting for auth initialization...', {
      isAuthInitializing,
      clerkIsLoaded,
      clerkIsSignedIn,
    });
    // Issue #3066: spinner 撲滅方針 — 初期化中は何も表示しない (PageLoadingSpinner は PR #3038 で null 化済み)
    return null;
  }

  // Check authentication only (NO subscription check)
  if (!isAuthenticated) {
    logger.info(
      '[AuthenticatedRoute] Not authenticated, redirecting to /login'
    );
    return <Navigate to="/login" replace />;
  }

  // Auth check passed - render children or Outlet
  logger.debug('[AuthenticatedRoute] Access granted (authentication only)');
  return <>{children ?? <Outlet />}</>;
};

export default AuthenticatedRoute;
